import type { ArrivalsBoard } from './types'

export interface FetchArrivalsOptions {
  /** API prefix; defaults to `/api/v2`. */
  baseUrl?: string
  timeoutMs?: number
  signal?: AbortSignal
}

/** Typed failure so hosts can distinguish transport from contract rot. */
export class ArrivalsError extends Error {
  kind: 'network' | 'timeout' | 'http' | 'parse'
  status?: number
  constructor(kind: ArrivalsError['kind'], message: string, status?: number) {
    super(message)
    this.name = 'ArrivalsError'
    this.kind = kind
    this.status = status
  }
}

interface Envelope {
  readonly data?: ArrivalsBoard
  readonly success?: boolean
}

/**
 * Fetches the station's live arrival board from
 * `GET /api/v2/arrivals/station/{naptanId}` — the London city server
 * (:8010); an unknown station is a 404 without a TfL call. Unwraps the
 * `{ data, success }` envelope. Throws {@link ArrivalsError}; hosts
 * decide their own degrade (the web section renders its empty state).
 */
export async function fetchArrivalsBoard(
  naptanId: string,
  opts: FetchArrivalsOptions = {},
): Promise<ArrivalsBoard> {
  const baseUrl = opts.baseUrl ?? '/api/v2'
  const timeout = opts.timeoutMs ?? 5000

  const controller = new AbortController()
  if (opts.signal !== undefined) {
    if (opts.signal.aborted) controller.abort()
    else opts.signal.addEventListener('abort', () => controller.abort())
  }
  const timer = setTimeout(() => controller.abort(), timeout)

  let res: Response
  try {
    res = await fetch(`${baseUrl}/arrivals/station/${encodeURIComponent(naptanId)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
  } catch (e: unknown) {
    clearTimeout(timer)
    if (controller.signal.aborted) {
      throw new ArrivalsError('timeout', `request timed out after ${timeout}ms`)
    }
    throw new ArrivalsError('network', String(e))
  }
  clearTimeout(timer)

  if (!res.ok) {
    throw new ArrivalsError('http', `arrivals returned ${res.status}`, res.status)
  }
  let envelope: Envelope
  try {
    envelope = (await res.json()) as Envelope
  } catch (e: unknown) {
    throw new ArrivalsError('parse', String(e))
  }
  if (envelope.success !== true || envelope.data === undefined) {
    throw new ArrivalsError('parse', 'envelope carries no data')
  }
  return envelope.data
}
